import { createSelector } from 'reselect';
import { oc } from 'ts-optchain';
import { RootState } from 'store/types';
import { currentAccountConsumerIdSelector } from 'store/account/selectors';
import { consumerIdSelector } from 'store/profile/selectors';
import { MapAvatar, LANGUAGE } from './types';

const consumerPreferencesSelector = (state: RootState) => state?.consumerPreferences;

export const consumerUiDataSelector = createSelector(
  consumerPreferencesSelector,
  consumerPreferences => consumerPreferences?.consumerUiData
);

export const consumerUiDataRequestStateSelector = createSelector(
  consumerUiDataSelector,
  consumerUiData => consumerUiData.requestState
);

export const consumerUiDataDataSelector = createSelector(
  consumerUiDataSelector,
  consumerUiData => consumerUiData?.data
);

export const consumerUiDataIsFetchingSelector = createSelector(
  consumerUiDataSelector,
  consumerUiData => consumerUiData?.isFetching
);

export const consumerPreferencesByIdSelector = createSelector(
  [consumerUiDataDataSelector, currentAccountConsumerIdSelector],
  (data, consumerId) => data?.find(x => x.consumerId === consumerId)
);

export const uiDataSelector = createSelector(consumerPreferencesByIdSelector, data =>
  oc(data).uiData()
);

// shared ui data selectors
export const sharedUiDataSelector = createSelector(uiDataSelector, uiData => uiData?.sharedUiData);

export const connectCareTermsAndConditionsSelector = createSelector(
  consumerPreferencesByIdSelector,
  data => oc(data).uiData.sharedUiData.hasAcceptedConnectCareTermsAndConditions(false)
);

export const hasSeenNotificationOptOutSelector = createSelector(
  consumerUiDataDataSelector,
  data => data?.[0]?.uiData?.sharedUiData?.hasSeenNotificationOptOut || false
);

export const isHealthToolSurveyCompletedSelector = createSelector(
  consumerUiDataDataSelector,
  data => data[0]?.uiData?.sharedUiData?.isHealthToolSurveyCompleted || false
);

export const avatarDataSelector = createSelector(
  consumerUiDataDataSelector,
  data => data?.[0]?.uiData?.sharedUiData?.avatars || []
);

// maps over all avatars
export const avatarDataMapSelector = createSelector(avatarDataSelector, avatars => {
  const map: MapAvatar = {};

  if (avatars) {
    for (const avatar of avatars) {
      map[avatar.consumerId] = avatar.avatarFileName;
    }
  }
  return map;
});

// gets the consumer user's avatar
export const consumerAvatarSelector = createSelector(
  [avatarDataMapSelector, consumerIdSelector],
  (avatarDataMap, consumerId) => avatarDataMap[consumerId]
);

// gets the consumer user's avatar
export const allConsumerAvatarSelector = createSelector(
  [avatarDataMapSelector],
  avatarDataMap => avatarDataMap
);

// gets the current user's avatar
export const currentUserAvatarSelector = createSelector(
  [avatarDataMapSelector, currentAccountConsumerIdSelector],
  (avatarDataMap, currentConsumerId) => avatarDataMap[currentConsumerId]
);

export const languageSelector = (state: RootState) => {
  const language = state?.consumerPreferences?.consumerUiData?.data[0]?.uiData?.sharedUiData?.preferredLanguage?.toLowerCase();
  if (language && (language === 'en' || language === 'es')) {
    return language;
  }
  return LANGUAGE.ENGLISH;
};
